import { useCallback, useEffect, useRef, useState } from 'react';
import { callApi, incomingCallStreamUrl } from '../../api/call.api';
import { usePolling } from '../../hooks/useDraft';
import { companyApi } from '../../api/company.api';
import { useAuth } from '../../store/AuthContext';
import { useUI } from '../../store/UIContext';
import { isEmployee } from '../../utils/roleHelpers';
import { Btn, Select } from '../ui/index';
import { ArrowUUpLeft, DeviceMobile } from '@phosphor-icons/react';

const AUTO_DISMISS_MS = 25000;
const POLL_MS = 5000;
const SSE_RECONNECT_MS = 4000;
const USE_INCOMING_CALL_SSE = process.env.REACT_APP_INCOMING_CALL_SSE === 'true';

function callerLabel(call) {
  if (call.matched_type === 'user' && call.matched_name) {
    return call.matched_company_name ? `${call.matched_name} (${call.matched_company_name})` : call.matched_name;
  }
  if (call.matched_type === 'company' && call.matched_company_name) {
    return call.matched_company_name;
  }
  return 'شماره ناشناس';
}

export default function IncomingCallListener() {
  const { user } = useAuth();
  const { addToast } = useUI();
  const enabled = isEmployee(user?.type);
  const [call, setCall] = useState(null);
  const [showTransfer, setShowTransfer] = useState(false);
  const [targetNumber, setTargetNumber] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [showSaveTrunk, setShowSaveTrunk] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [trunkCompanyId, setTrunkCompanyId] = useState('');
  const [savingTrunk, setSavingTrunk] = useState(false);
  const dismissTimerRef = useRef(null);
  const lastSeenCallIdRef = useRef(0);
  const bootstrappedRef = useRef(false);
  const canSaveTrunk = user?.type === 'dispatcher';

  const pollCalls = useCallback(async () => {
    if (!enabled || USE_INCOMING_CALL_SSE) return;
    try {
      const res = await callApi.getRecentCallEvents(8);
      const events = Array.isArray(res.data?.events) ? res.data.events : [];
      if (!events.length) return;
      const maxId = Math.max(...events.map((e) => Number(e.id) || 0));
      if (!bootstrappedRef.current) {
        bootstrappedRef.current = true;
        lastSeenCallIdRef.current = maxId;
        return;
      }
      const fresh = events
        .filter((e) => (Number(e.id) || 0) > lastSeenCallIdRef.current)
        .sort((a, b) => (Number(a.id) || 0) - (Number(b.id) || 0));
      if (!fresh.length) return;
      lastSeenCallIdRef.current = Math.max(lastSeenCallIdRef.current, maxId);
      setCall(fresh[fresh.length - 1]);
    } catch {
      // keep polling
    }
  }, [enabled]);

  usePolling(pollCalls, POLL_MS, enabled && !USE_INCOMING_CALL_SSE);

  useEffect(() => {
    if (!enabled || !USE_INCOMING_CALL_SSE) return undefined;

    let es;
    let reconnectTimer;

    const connect = () => {
      es = new EventSource(incomingCallStreamUrl(), { withCredentials: true });
      es.onmessage = (e) => {
        try {
          const event = JSON.parse(e.data);
          if (event.type === 'incoming_call') {
            setCall(event);
          }
        } catch {
          // ignore non-JSON lines
        }
      };
      es.onerror = () => {
        es?.close();
        reconnectTimer = setTimeout(connect, SSE_RECONNECT_MS);
      };
    };
    connect();

    return () => {
      es?.close();
      clearTimeout(reconnectTimer);
    };
  }, [enabled]);

  useEffect(() => {
    clearTimeout(dismissTimerRef.current);
    if (call && !showTransfer && !showSaveTrunk) {
      dismissTimerRef.current = setTimeout(() => setCall(null), AUTO_DISMISS_MS);
    }
    return () => clearTimeout(dismissTimerRef.current);
  }, [call, showTransfer, showSaveTrunk]);

  useEffect(() => {
    if (!call) {
      setShowTransfer(false);
      setTargetNumber('');
      setIsMobile(false);
      setShowSaveTrunk(false);
      setTrunkCompanyId('');
    }
  }, [call]);

  const handleShowSaveTrunk = async () => {
    setShowSaveTrunk(true);
    if (companies.length === 0) {
      try {
        const res = await companyApi.getCompanyList();
        setCompanies(res.data || []);
      } catch {
        addToast('خطا در دریافت لیست شرکت‌ها', 'error');
      }
    }
  };

  const handleSaveTrunk = async () => {
    if (!trunkCompanyId) {
      addToast('شرکت را انتخاب کنید', 'error');
      return;
    }
    setSavingTrunk(true);
    try {
      await callApi.saveTrunkMapping({ trunk_name: call.trunk_name, label: '', company_id: trunkCompanyId });
      addToast('ترانک ذخیره شد', 'success');
      setShowSaveTrunk(false);
    } catch (e) {
      addToast(e.response?.data?.error || 'خطا در ذخیره ترانک', 'error');
    } finally {
      setSavingTrunk(false);
    }
  };

  const handleTransfer = async () => {
    if (!targetNumber.trim()) {
      addToast('شماره مقصد را وارد کنید', 'error');
      return;
    }
    setTransferring(true);
    try {
      await callApi.transferCall(call.id, targetNumber.trim(), isMobile);
      addToast('تماس منتقل شد', 'success');
      setCall(null);
    } catch (e) {
      addToast(e.response?.data?.error || 'خطا در انتقال تماس', 'error');
    } finally {
      setTransferring(false);
    }
  };

  const handleTransferToMe = async () => {
    setTransferring(true);
    try {
      await callApi.transferCallToMe(call.id);
      addToast('تماس به شماره شما منتقل شد', 'success');
      setCall(null);
    } catch (e) {
      addToast(e.response?.data?.error || 'خطا در انتقال تماس', 'error');
    } finally {
      setTransferring(false);
    }
  };

  if (!enabled || !call) return null;

  return (
    <div
      className={`fade-in pj-incoming-call-box${showTransfer || showSaveTrunk ? ' is-still' : ''}`}
      style={{
        position: 'fixed', bottom: 20, left: 20, zIndex: 1000,
        width: showTransfer || showSaveTrunk ? 320 : 300, background: '#fff', borderRadius: 14,
        border: '1px solid #bbf7d0', boxShadow: '0 10px 32px rgba(22, 163, 74, 0.22)',
        padding: '16px 18px', display: 'flex', gap: 12, alignItems: 'flex-start',
      }}
    >
      <div style={{ fontSize: 26 }}>📞</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', marginBottom: 2 }}>تماس ورودی</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {callerLabel(call)}
        </div>
        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>
          {call.caller_number || 'شماره نامشخص'}
        </div>
        {canSaveTrunk && call.trunk_name && call.matched_type !== 'company' && !showSaveTrunk && (
          <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>
            ترانک: {call.trunk_name} — شرکتی برای آن ثبت نشده
          </div>
        )}
        {!showTransfer && !showSaveTrunk ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Btn variant="ghost" size="sm" onClick={() => setCall(null)}>باشه</Btn>
            <Btn variant="primary" size="sm" onClick={() => setShowTransfer(true)}><ArrowUUpLeft size={14} weight="regular" /> انتقال تماس</Btn>
            <Btn variant="ghost" size="sm" loading={transferring} onClick={handleTransferToMe}><DeviceMobile size={14} weight="regular" /> انتقال به من</Btn>
            {canSaveTrunk && call.trunk_name && call.matched_type !== 'company' && (
              <Btn variant="ghost" size="sm" onClick={handleShowSaveTrunk}>🏭 ذخیره ترانک</Btn>
            )}
          </div>
        ) : showSaveTrunk ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>ترانک «{call.trunk_name}» مال کدام شرکت است؟</div>
            <Select
              value={trunkCompanyId}
              onChange={(e) => setTrunkCompanyId(e.target.value)}
              options={[{ value: '', label: '— انتخاب شرکت —' }, ...companies.map((c) => ({ value: c.id, label: c.display_name || c.name }))]}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn variant="primary" size="sm" loading={savingTrunk} onClick={handleSaveTrunk}>ذخیره</Btn>
              <Btn variant="ghost" size="sm" onClick={() => setShowSaveTrunk(false)}>انصراف</Btn>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input
              value={targetNumber}
              onChange={(e) => setTargetNumber(e.target.value)}
              placeholder="داخلی یا شماره موبایل"
              style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}
              autoFocus
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#64748b' }}>
              <input type="checkbox" checked={isMobile} onChange={(e) => setIsMobile(e.target.checked)} />
              شماره موبایل بیرون از سازمان است
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn variant="primary" size="sm" loading={transferring} onClick={handleTransfer}>انتقال</Btn>
              <Btn variant="ghost" size="sm" onClick={() => setShowTransfer(false)}>انصراف</Btn>
            </div>
          </div>
        )}
      </div>
      <button
        onClick={() => setCall(null)}
        aria-label="بستن"
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 15, lineHeight: 1, padding: 0 }}
      >
        ✕
      </button>
    </div>
  );
}
